import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { useBooking } from '../context/BookingContext';
import Navigation from '../components/Navigation';
import ElegantPageBg from '../components/ElegantPageBg';
import { FiArrowLeft, FiMapPin, FiClock, FiCalendar, FiCheck, FiZap, FiDroplet } from 'react-icons/fi';
import { locationService, ticketService } from '../services/api';
import { ensureMediaOriginLoaded } from '../services/siteConfig';
import { toYmd, isEventTicketType } from '../utils/dateRange';
import { resolveMediaUrl } from '../utils/mediaUrl';
import './DateSelection.css';
import 'react-datepicker/dist/react-datepicker.css';

/** Local calendar day at 00:00:00 for comparisons with react-datepicker. */
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function dateToYmdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Every YYYY-MM-DD that falls inside any event ticket type’s event_date_range (inclusive). */
function buildEventAvailableYmdSet(locationTicketPrices) {
  const set = new Set();
  for (const tp of locationTicketPrices) {
    const tt = tp.ticket_type;
    if (!isEventTicketType(tt) || !tt.event_date_range?.from || !tt.event_date_range?.to) continue;
    const fr = toYmd(tt.event_date_range.from);
    const to = toYmd(tt.event_date_range.to);
    if (!fr || !to) continue;
    const [fy, fm, fd] = fr.split('-').map(Number);
    const [ty, tm, td] = to.split('-').map(Number);
    let cur = startOfDay(new Date(fy, fm - 1, fd));
    const end = startOfDay(new Date(ty, tm - 1, td));
    if (cur > end) continue;
    while (cur <= end) {
      set.add(dateToYmdLocal(cur));
      const next = new Date(cur);
      next.setDate(next.getDate() + 1);
      cur = startOfDay(next);
    }
  }
  return set;
}

const DateSelection = () => {
  const { bookingData, locationReady, updateVisitDate } = useBooking();
  const [selectedDate, setSelectedDate] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [locationTicketPrices, setLocationTicketPrices] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 600);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const loc = bookingData.location;
    if (!loc?.id) {
      setLocationTicketPrices([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await ensureMediaOriginLoaded();
        const res = await locationService.getTickets(loc.id);
        let list = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
        if (list.length === 0) {
          try {
            const typesRes = await ticketService.getAll();
            const typesData = typesRes.data;
            const types = Array.isArray(typesData) ? typesData : (typesData?.results ?? []);
            list = types.map((tt) => ({
              ticket_type: tt,
              ticket_type_id: tt.id,
              price: tt.base_price,
            }));
          } catch (e) {
            console.warn('Could not load ticket types for event banner:', e);
          }
        }
        if (!cancelled) setLocationTicketPrices(list);
      } catch (e) {
        console.warn('Could not load tickets for event banner:', e);
        if (!cancelled) setLocationTicketPrices([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookingData.location]);

  const eventAvailableYmdSet = useMemo(
    () => buildEventAvailableYmdSet(locationTicketPrices),
    [locationTicketPrices]
  );

  const eventDayClassName = (date) =>
    eventAvailableYmdSet.has(dateToYmdLocal(date)) ? 'calendar-day--has-event' : undefined;

  const eventBannerUrl = useMemo(() => {
    if (!locationTicketPrices.length || !selectedDate) return null;

    const y = selectedDate.getFullYear();
    const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const d = String(selectedDate.getDate()).padStart(2, '0');
    const visitStr = `${y}-${m}-${d}`;

    for (const tp of locationTicketPrices) {
      const tt = tp.ticket_type;
      if (!isEventTicketType(tt) || !tt.event_date_range?.from || !tt.event_date_range?.to) continue;
      const fr = toYmd(tt.event_date_range.from);
      const to = toYmd(tt.event_date_range.to);
      if (fr && to && visitStr >= fr && visitStr <= to && tt.event_banner) {
        return tt.event_banner;
      }
    }

    return null;
  }, [selectedDate, locationTicketPrices]);

  // Recompute each render so “today” stays correct if the tab crosses midnight.
  const minSelectableDate = startOfDay(new Date());
  const isDateSelectable = (date) => startOfDay(date) >= minSelectableDate;

  const handleDateChange = (date) => {
    if (!date) {
      setSelectedDate(null);
      return;
    }
    if (!isDateSelectable(date)) return;
    setSelectedDate(date);
  };

  const handleContinue = () => {
    if (selectedDate) {
      // Use local date components to avoid UTC shift (toISOString would give wrong date in IST)
      const y = selectedDate.getFullYear();
      const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const d = String(selectedDate.getDate()).padStart(2, '0');
      updateVisitDate(`${y}-${m}-${d}`);
      navigate('/tickets');
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  if (!locationReady) {
    return (
      <div className="date-selection-page">
        <ElegantPageBg />
        <Navigation />
        <div className="date-selection-container">
          <div className="loading-container" style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
            <div className="loading-spinner" />
            <p>Loading Chennai location...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!bookingData.location) {
    return (
      <div className="date-selection-page">
        <ElegantPageBg />
        <Navigation />
        <div className="date-selection-container">
          <p style={{ textAlign: 'center', color: '#64748b', padding: '60px 20px' }}>
            No location available. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  const location = bookingData.location;
  const today = new Date();
  const isToday = (date) => {
    return date.toDateString() === today.toDateString();
  };

  return (
    <div className="date-selection-page">
      <ElegantPageBg />
      <Navigation />
      
      <div className="date-selection-container">
        <div className={`date-selection-grid ${isAnimating ? 'animating' : ''}`}>
          {/* Left Panel - Location Info */}
          <div className="location-info-panel">
            <div className="panel-header">
              <div className="header-content">
                <h1 className="panel-title">Plan Your Adventure</h1>
                <div className="location-tag">
                  <FiMapPin />
                  <span>{location.city}</span>
                </div>
              </div>
            </div>

            <div className="location-details">
              <div className="location-name-section">
                {location.name !== location.city && (
                  <h2 className="location-name">{location.name}</h2>
                )}
                <p className="location-description">
                  {location.description || 'Experience thrilling rides, exciting attractions, and unforgettable memories at one of the most popular amusement parks!'}
                </p>
              </div>

              <div className="timings-card">
                <div className="timings-header">
                  <div className="timings-icon-wrapper">
                    <FiClock className="timings-icon" />
                  </div>
                  <h3 className="timings-title">Operating Hours</h3>
                </div>
                
                <div className="timings-grid">
                  <div className="timing-block weekday-block">
                    <div className="timing-label">
                      <span className="day-type">Weekdays</span>
                      <span className="day-badge">Mon - Fri</span>
                    </div>
                    <div className="timing-details">
                      <div className="timing-item">
                        <div className="timing-icon-inner park">
                          <FiZap />
                        </div>
                        <div className="timing-content">
                          <span className="timing-type">Park Rides</span>
                          <span className="timing-value">{location.park_timings_weekdays}</span>
                        </div>
                      </div>
                      <div className="timing-item">
                        <div className="timing-icon-inner water">
                          <FiDroplet />
                        </div>
                        <div className="timing-content">
                          <span className="timing-type">Water Rides</span>
                          <span className="timing-value">{location.water_timings_weekdays}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="timing-block weekend-block">
                    <div className="timing-label">
                      <span className="day-type">Weekends</span>
                      <span className="day-badge">Sat - Sun</span>
                    </div>
                    <div className="timing-details">
                      <div className="timing-item">
                        <div className="timing-icon-inner park">
                          <FiZap />
                        </div>
                        <div className="timing-content">
                          <span className="timing-type">Park Rides</span>
                          <span className="timing-value">{location.park_timings_weekends}</span>
                        </div>
                      </div>
                      <div className="timing-item">
                        <div className="timing-icon-inner water">
                          <FiDroplet />
                        </div>
                        <div className="timing-content">
                          <span className="timing-type">Water Rides</span>
                          <span className="timing-value">{location.water_timings_weekends}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {eventBannerUrl ? (
                  <div className="event-banner-below-hours">
                    <img
                      src={resolveMediaUrl(eventBannerUrl) || eventBannerUrl}
                      alt=""
                      className="event-banner-below-hours__img"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Right Panel - Calendar */}
          <div className="calendar-panel">
            <div className="calendar-header">
              <div className="calendar-header-content">
                <div className="calendar-icon-wrapper">
                  <FiCalendar className="calendar-icon" />
                </div>
                <div>
                  <h2 className="calendar-title">Select Your Visit Date</h2>
                  <p className="calendar-subtitle">Choose the perfect day for your adventure</p>
                  {eventAvailableYmdSet.size > 0 ? (
                    <p className="calendar-event-legend" aria-hidden="true">
                      <span className="calendar-event-legend__dot" /> Event days
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="calendar-wrapper">
              <DatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                minDate={minSelectableDate}
                filterDate={isDateSelectable}
                dayClassName={eventDayClassName}
                inline
                fixedHeight
                calendarClassName="modern-calendar"
                formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 3)}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                showOutsideDays={false}
              />
            </div>

            {selectedDate && (
              <div className="selected-date-info">
                <div className="selected-date-card">
                  <div className="selected-check">
                    <FiCheck />
                  </div>
                  <div className="date-display">
                    <div className="date-main">
                      <span className="date-day">{selectedDate.getDate()}</span>
                      <div className="date-month-year">
                        <span className="date-month">
                          {selectedDate.toLocaleDateString('en-US', { month: 'short' })}
                        </span>
                        <span className="date-year">{selectedDate.getFullYear()}</span>
                      </div>
                    </div>
                    <div className="date-divider"></div>
                    <div className="date-day-name">
                      {selectedDate.toLocaleDateString('en-US', { weekday: 'long' })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button
              className={`continue-btn ${selectedDate ? 'active' : 'disabled'}`}
              onClick={handleContinue}
              disabled={!selectedDate}
            >
              <span>Continue to Tickets</span>
              <div className="btn-arrow">
                <FiArrowLeft style={{ transform: 'rotate(180deg)' }} />
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateSelection;
